import type {
  AnnotationHandler,
  CodeAnnotation,
  HighlightedCode,
} from "codehike/code";
import { useEffect, useRef, useState } from "react";
import { highlight, Pre } from "codehike/code";

import { useLighterTheme } from "./_utils/lighter-theme";
import { HighlighterErrorBoundary } from "./code-highlighter-error-boundary";

export const matchHandler: AnnotationHandler = {
  name: "match",
  Block: ({ children }) => <div className="bg-yellow-400/20">{children}</div>,
  Inline: ({ children }) => (
    <span className="bg-yellow-400/40">{children}</span>
  ),
};

export function CodeHighlightWithMatches({
  code,
  matches,
}: {
  code: string;
  matches: string[];
}) {
  const [highlightedCode, setHighlightedCode] =
    useState<HighlightedCode | null>(null);
  const highlighterTheme = useLighterTheme();
  const isFirstRun = useRef(true);
  const animationFrameRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const performHighlight = async () => {
      const baseHighlightedCode = await highlight(
        {
          lang: "typescript",
          value: code,
          meta: "",
        },
        highlighterTheme,
      );

      const generateAnnotations = (code: string, matches: string[]) => {
        const annotations: CodeAnnotation[] = [];
        const allLines = code.split("\n");

        for (const match of matches) {
          const matchIndex = code.indexOf(match);
          if (matchIndex === -1) continue;

          // Split the code up to the match to count lines
          const beforeMatch = code.slice(0, matchIndex);
          const linesBefore = beforeMatch.split("\n");
          const startLine = linesBefore.length;

          // Split the match to count its lines
          const currentMatchAnnotations = match
            .split("\n")
            .map((line, index, arr) => {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              const currentFullLine = allLines[startLine + index - 1]!;
              const fromColumn =
                index === 0 ? currentFullLine.indexOf(line) + 1 : 1;
              const toColumn =
                index === arr.length - 1
                  ? fromColumn + line.length - 1
                  : currentFullLine.length;

              return {
                lineNumber: startLine + index,
                fromColumn,
                toColumn,
                name: "match",
                query: "match",
                data: {},
              };
            });

          annotations.push(...currentMatchAnnotations);
        }

        return annotations;
      };

      const annotations = generateAnnotations(code, matches);
      setHighlightedCode({ ...baseHighlightedCode, annotations });
    };

    // Cancel any pending animation frame
    if (animationFrameRef.current !== undefined) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    if (isFirstRun.current) {
      isFirstRun.current = false;
      void performHighlight();
    } else {
      animationFrameRef.current = requestAnimationFrame(() => {
        void performHighlight();
      });
    }

    return () => {
      if (animationFrameRef.current !== undefined) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [code, matches, highlighterTheme]);

  if (highlightedCode === null) {
    return null;
  }

  return (
    <HighlighterErrorBoundary>
      <Pre
        className="bg-background rounded-lg border px-4 py-3 text-xs whitespace-pre-wrap"
        code={highlightedCode}
        handlers={[matchHandler]}
      />
    </HighlighterErrorBoundary>
  );
}
